/**
 * Pinned one-line copy of the scenario summary numbers. Founder UAT
 * 2026-09-03: editing a price far down the calibration list moved the top
 * totals, but they had scrolled away, so the impact was invisible. The full
 * ScenarioSummaryCards stay where they are (their helper copy came out of
 * the 2026-08-30 UAT); this strip pins under the app bar only once those
 * cards have scrolled off, so the numbers are never duplicated on screen.
 *
 * "Change from today's prices" is the number an edit moves, so it gets the
 * emphasis and a short highlight whenever it changes. Pure props in,
 * markup out; the "are the cards off screen" question is answered by the
 * page (IntersectionObserver), not here, so this stays testable in a
 * static render.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../design';
import {
  money,
  signedMoney,
  type PriceCalibrationScenarioSummary,
} from '../../lib/price-calibration';

const FLASH_MS = 900;

export function ScenarioSummaryStrip({
  summary,
  includedRowCount,
  visible,
}: {
  summary: PriceCalibrationScenarioSummary;
  includedRowCount: number;
  visible: boolean;
}) {
  const { financial } = summary.aggregate;
  const priced = financial.valuedRowCount > 0;
  const delta = financial.sellThroughProfitDeltaCents;

  // Highlight the delta briefly when an edit moves it. Skipped on the first
  // render so the strip does not flash just for appearing.
  const [flash, setFlash] = useState(false);
  const prevDelta = useRef(delta);
  useEffect(() => {
    if (prevDelta.current === delta) return;
    prevDelta.current = delta;
    setFlash(true);
    const t = window.setTimeout(() => setFlash(false), FLASH_MS);
    return () => window.clearTimeout(t);
  }, [delta]);

  const deltaTone = !priced
    ? 'text-fg-primary'
    : delta > 0
      ? 'text-positive'
      : delta < 0
        ? 'text-negative'
        : 'text-fg-primary';

  return (
    <div
      aria-hidden={visible ? undefined : 'true'}
      className={cn(
        'sticky top-0 md:top-14 z-20 -mx-6 sm:-mx-8 px-6 sm:px-8 py-2',
        'bg-canvas/95 backdrop-blur border-b border-border-subtle',
        !visible && 'hidden'
      )}
    >
      <dl className="flex items-baseline gap-x-6 gap-y-1 flex-wrap text-xs text-fg-tertiary tabular-nums">
        <Stat label="Units in view" value={summary.selectedUnits.toLocaleString('en-US')}>
          <span className="hidden sm:inline">
            {' '}
            · {includedRowCount.toLocaleString('en-US')} product{includedRowCount === 1 ? '' : 's'}
          </span>
        </Stat>
        <Stat
          label="Profit at these prices"
          value={priced ? money(financial.remainingInventoryProfitCents) : '—'}
        />
        <Stat
          label="Change from today"
          value={priced ? signedMoney(delta) : '—'}
          valueClassName={cn(
            'text-base',
            deltaTone,
            flash && 'motion-safe:animate-pulse bg-accent-primary/15 rounded-sm px-1 -mx-1'
          )}
        />
        <Stat label="Time to sell" value={summary.blendedRunway.durationLabel} />
      </dl>
    </div>
  );
}

function Stat({
  label,
  value,
  valueClassName,
  children,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <dt className="whitespace-nowrap">{label}</dt>
      <dd
        className={cn(
          'font-display text-sm text-fg-primary whitespace-nowrap transition-colors duration-fast',
          valueClassName
        )}
      >
        {value}
        {children}
      </dd>
    </div>
  );
}
